import type React from "react";
import type { ComponentType } from "react";

import type { CSSProperties as LinariaCSSProperties } from "@linaria/core";
import { css, cx } from "@linaria/core";
import { styled as linariaStyled } from "@linaria/react";

import { withTheme } from "./theme/themeProvider";
import type { UseThemeType } from "./theme/types";

export type DefaultTheme = UseThemeType;
export type CSSProperties = LinariaCSSProperties;
export type Keyframes = string;

type ThemeProps = { theme: DefaultTheme };

type WYWEvalMeta = { __wyw_meta: unknown };

type IntrinsicElements = React.JSX.IntrinsicElements & JSX.IntrinsicElements;

type StyledComponentType<T> = WYWEvalMeta &
  ([T] extends [React.FunctionComponent<unknown>]
    ? T
    : React.FunctionComponent<
        T & { as?: React.ElementType; theme?: DefaultTheme }
      >);

type StaticPlaceholder = string | number | LinariaCSSProperties | WYWEvalMeta;

type WithOptionalTheme<T> = T & {
  as?: React.ElementType;
  theme?: DefaultTheme;
} & Record<string, unknown>;

type HtmlStyledTag<TName extends keyof IntrinsicElements> = <
  TAdditionalProps = Record<never, unknown>,
>(
  strings: TemplateStringsArray,
  ...exprs: Array<
    | StaticPlaceholder
    | ((
        props: IntrinsicElements[TName] &
          Omit<TAdditionalProps, never> &
          ThemeProps,
      ) => string | number | false | null | undefined)
  >
) => StyledComponentType<
  WithOptionalTheme<IntrinsicElements[TName] & TAdditionalProps>
>;

export type StyledJSXIntrinsics = {
  readonly [P in keyof IntrinsicElements]: HtmlStyledTag<P>;
};

type ComponentStyledTag<TComponent> = <
  TAdditionalProps extends Record<string, unknown> = Record<never, unknown>,
>(
  strings: TemplateStringsArray,
  ...exprs: Array<
    | StaticPlaceholder
    | ((
        props: Omit<TAdditionalProps, never> & ThemeProps,
      ) => string | number | false | null | undefined)
  >
) => TComponent extends React.ComponentType<infer P>
  ? React.ComponentType<
      P & TAdditionalProps & { as?: React.ElementType; theme?: DefaultTheme }
    > &
      TComponent
  : WYWEvalMeta & TComponent;

declare function styledCallable<TComponent>(
  component: TComponent,
): ComponentStyledTag<TComponent>;

declare function styledCallable<TName extends keyof IntrinsicElements>(
  tag: TName,
): HtmlStyledTag<TName>;

export type Styled = typeof styledCallable & StyledJSXIntrinsics;

type ThemedComponent = ComponentType<Record<string, unknown>>;

function wrapWithTheme<C extends ThemedComponent>(Component: C): C {
  return withTheme(Component as ComponentType<{ theme: UseThemeType }>) as C;
}

function createThemedTag(tagOrComponent: string | ThemedComponent) {
  const factory =
    typeof tagOrComponent === "string"
      ? linariaStyled(tagOrComponent as keyof JSX.IntrinsicElements)
      : linariaStyled(tagOrComponent);

  return (strings: TemplateStringsArray, ...values: unknown[]) =>
    wrapWithTheme(factory(strings, ...(values as never[])) as ThemedComponent);
}

const styledBase = (tagOrComponent: string | ThemedComponent) =>
  createThemedTag(tagOrComponent);

export const styled = new Proxy(styledBase, {
  get(_target, prop: string | symbol) {
    if (typeof prop === "string") {
      return createThemedTag(prop);
    }
    return undefined;
  },
}) as Styled;

export { css, cx };

/** Runtime stub — wyw extracts keyframes at build time. */
export function keyframes(
  strings: TemplateStringsArray,
  ...values: unknown[]
): Keyframes {
  void strings;
  void values;
  return "";
}
